// The piano's voice, generated, because the skill has to be AUDIBLE.
//
// No samples and no library: a plinky felt-piano tone is two detuned
// triangle partials and a fast decay through the existing audio context.
// What makes it the feature is that the GENERATOR reads his skill, the
// same number the sim grew on disk and streamed in the world delta:
//
//   plinking  - hesitant single notes, long gaps, the odd sour neighbour
//   picking   - a slow melodic line that sometimes restarts
//   pieces    - a flowing pentatonic line with a left-hand root
//   tunes     - melody over chords, phrases that breathe
//
// A visitor standing near the piano on week one and again on week three
// hears the difference, which is the whole point Sam asked for: the
// learning is real, so the sound of it must be too.
//
// Distance does the mixing: gain falls with the square of distance and a
// lowpass closes in, so far away he is a feeling and up close he is a
// bird at a keyboard. The caller feeds distance every frame; this module
// never touches the scene.

import { getCtx, audioReady } from './context'
import { phrase, mulberry, score } from './pianoScore'

// The composition lives in pianoScore.js so the offline renderer plays
// exactly what the browser plays. What stays here is the SOUND: two
// detuned partials and a fast decay, which is the felt-piano tone.

let playing = null // { master, filter, timer, seed }

function note(ctx, dest, freq, when, dur, gain) {
  const osc = ctx.createOscillator()
  const partial = ctx.createOscillator()
  const env = ctx.createGain()
  osc.type = 'triangle'
  partial.type = 'sine'
  osc.frequency.value = freq
  // The slightly sharp octave partial is what reads as struck string
  // rather than organ.
  partial.frequency.value = freq * 2.003
  const pGain = ctx.createGain()
  pGain.gain.value = 0.25
  env.gain.setValueAtTime(0, when)
  env.gain.linearRampToValueAtTime(gain, when + 0.01)
  env.gain.exponentialRampToValueAtTime(0.0001, when + dur)
  osc.connect(env)
  partial.connect(pGain).connect(env)
  env.connect(dest)
  osc.start(when); partial.start(when)
  osc.stop(when + dur + 0.05); partial.stop(when + dur + 0.05)
}

/**
 * Start the piano playing at a tier. Idempotent per call site: call
 * stop() first if the tier changed. Distance mixing happens through
 * setDistance, every frame, cheap.
 */
export function startPiano(tier, seed = 1) {
  if (!audioReady() || playing) return
  const ctx = getCtx()
  const master = ctx.createGain()
  master.gain.value = 0
  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 8000
  master.connect(filter).connect(ctx.destination)
  const rand = mulberry(seed * 2654435761)
  let horizon = ctx.currentTime + 0.1
  const timer = setInterval(() => {
    // Keep about four seconds of music scheduled ahead; phrases breathe
    // on their own clock. The notes come from the shared score, so what
    // is heard here and what a wav renders later cannot drift apart.
    while (horizon < ctx.currentTime + 4) {
      const bar = []
      horizon = phrase(bar, tier, rand, horizon)
      for (const n of bar) note(ctx, master, n.freq, n.at, n.dur, n.gain)
    }
  }, 500)
  playing = { master, filter, timer }
}

/**
 * A piece he wrote (sim world/Pieces.js): two hands as timed events,
 * looped for as long as he sits, through the same felt voice. Replaces
 * the generator from the pieces stage up; the distance mixing is the
 * same, because `playing` has the same shape.
 */
export function startPiece(piece) {
  if (!piece?.events?.length || !audioReady()) return
  if (playing?.pieceId === piece.id) return
  stopPiano()
  const ctx = getCtx()
  const master = ctx.createGain()
  master.gain.value = 0
  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 8000
  master.connect(filter).connect(ctx.destination)
  const seconds = Math.max(1, Number(piece.seconds) || 16)
  let cycleStart = ctx.currentTime + 0.1
  const schedule = () => {
    for (const e of piece.events) {
      const dur = Math.max(0.05, Number(e.dur) || 0.25)
      note(ctx, master, e.freq, cycleStart + (Number(e.at) || 0), Math.max(0.12, dur * 1.6), Math.max(0.05, Math.min(1, Number(e.gain) || 0.7)) * 0.5)
    }
    cycleStart += seconds
  }
  schedule(); schedule()
  const timer = setInterval(() => {
    if (!playing) return
    if (cycleStart - ctx.currentTime < seconds * 1.5) schedule()
  }, 500)
  playing = { master, filter, timer, pieceId: piece.id }
}
export function stopPiano() {
  if (!playing) return
  clearInterval(playing.timer)
  const { master } = playing
  try {
    const ctx = getCtx()
    master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4)
    setTimeout(() => { try { master.disconnect() } catch { /* gone */ } }, 600)
  } catch { /* context torn down */ }
  playing = null
}

export function isPianoPlaying() { return !!playing }

// ── The archive ─────────────────────────────────────────────────────────
//
// One sitting, played straight through at full volume, for the page that
// keeps a record of his playing. Same voice and same score as the world
// hears; only the distance mixing is missing, because nobody is standing
// anywhere. Returns a stop function.

export function playSitting(tier, seed, seconds = 40) {
  if (!audioReady()) return () => {}
  const ctx = getCtx()
  const master = ctx.createGain()
  master.gain.value = 0.8
  master.connect(ctx.destination)
  const notes = score(tier, seed, seconds)
  const t0 = ctx.currentTime + 0.08
  for (const n of notes) note(ctx, master, n.freq, t0 + n.at, n.dur, n.gain)
  let stopped = false
  return () => {
    if (stopped) return
    stopped = true
    try {
      master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.25)
      setTimeout(() => { try { master.disconnect() } catch { /* gone */ } }, 400)
    } catch { /* context torn down */ }
  }
}

/**
 * The same sitting as a downloadable wav, rendered offline in the
 * browser so the page can hand back a file without a build step. The
 * voice is rebuilt here through OfflineAudioContext rather than shared
 * with note() above, because that one schedules against a live clock.
 */
export async function renderSittingWav(tier, seed, seconds = 40) {
  const notes = score(tier, seed, seconds)
  const rate = 44100
  const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext
  const off = new Offline(1, Math.ceil(seconds * rate), rate)
  const master = off.createGain()
  master.gain.value = 0.8
  master.connect(off.destination)
  for (const n of notes) note(off, master, n.freq, n.at + 0.05, n.dur, n.gain)
  const buf = await off.startRendering()
  const data = buf.getChannelData(0)
  const pcm = new DataView(new ArrayBuffer(44 + data.length * 2))
  const str = (at, t) => { for (let i = 0; i < t.length; i++) pcm.setUint8(at + i, t.charCodeAt(i)) }
  str(0, 'RIFF'); pcm.setUint32(4, 36 + data.length * 2, true); str(8, 'WAVE')
  str(12, 'fmt '); pcm.setUint32(16, 16, true); pcm.setUint16(20, 1, true)
  pcm.setUint16(22, 1, true); pcm.setUint32(24, rate, true)
  pcm.setUint32(28, rate * 2, true); pcm.setUint16(32, 2, true); pcm.setUint16(34, 16, true)
  str(36, 'data'); pcm.setUint32(40, data.length * 2, true)
  for (let i = 0; i < data.length; i++) {
    pcm.setInt16(44 + i * 2, Math.max(-1, Math.min(1, data[i])) * 32767, true)
  }
  return new Blob([pcm.buffer], { type: 'audio/wav' })
}

/** Distance in world units to gain and tone. ~4 units = at the stool. */
export function setPianoDistance(d) {
  if (!playing) return
  const ctx = getCtx()
  const near = 4
  const far = 60
  const t = Math.max(0, Math.min(1, (d - near) / (far - near)))
  const gain = (1 - t) * (1 - t) * 0.8
  playing.master.gain.setTargetAtTime(gain, ctx.currentTime, 0.15)
  playing.filter.frequency.setTargetAtTime(800 + (1 - t) * 7200, ctx.currentTime, 0.2)
}
